import request from 'api/requestUtils'
import config from 'constants/config'
import calculateDistance from 'lib/geo/distanceBetweenPoints'
import { isNumber } from 'lib/maths/mathUtils'
import { reportLog } from 'services/userMonitoring'

export type TravelMode = 'DRIVE' | 'WALK'

export type RouteSummary = {
  duration: string | undefined
  distance: number
  travelMode: TravelMode
  encodedPolyline?: string
}

export const DISTANCE_THRESHOLD_BETWEEN_WALK_AND_DRIVE = 2
const MAP_BOX_HOST = 'https://api.mapbox.com'
const MAP_BOX_BASE = '/directions/v5/mapbox'

type MapboxDirectionsResponse = {
  routes?: Array<{
    duration?: number
    distance?: number
    geometry?: string
  }>
}

export async function getRouteInfo(
  origin: { lat: number, lng: number },
  destination: { lat: number, lng: number },
  travelMode?: TravelMode,
): Promise<RouteSummary | undefined> {
  try {
    const greatCircleKm = calculateDistance(origin.lat, origin.lng, destination.lat, destination.lng)
    const selectedMode: TravelMode =
      travelMode || (greatCircleKm > DISTANCE_THRESHOLD_BETWEEN_WALK_AND_DRIVE ? 'DRIVE' : 'WALK')

    const profile = selectedMode === 'DRIVE' ? 'driving' : 'walking'
    const coords = `${origin.lng},${origin.lat};${destination.lng},${destination.lat}`

    const path =
      `${MAP_BOX_BASE}/${profile}/${coords}` +
      `?overview=full&geometries=polyline&exclude=ferry&access_token=${config.MAP_BOX_API_KEY}`

    const res = await request.get<MapboxDirectionsResponse>(path, { excludeBrand: true }, MAP_BOX_HOST)
    const route = res?.routes?.[0]
    if (!route) return undefined

    const duration = isNumber(route.duration) ? `${Math.round(route.duration)}s` : undefined
    const km = isNumber(route.distance) ? route.distance / 1000 : greatCircleKm

    return { duration, distance: km, travelMode: selectedMode, encodedPolyline: route.geometry }
  } catch(error) {
    reportLog('error fetching trip planner route summary', { error })
    return undefined
  }
}
